import { formatDateTime } from '@/utils'

/**
 * Date and time rendering for the Zocdoc screens, in the shapes the Schedule page uses:
 * **`MM/DD/YYYY`** for a date (`formatDateTime` in `src/utils.ts`) and **`h:mm a`** lowercase for a
 * time (`fmtTime` in `Schedule/components/scheduleCalendar.tokens.ts`).
 *
 * **These change the shape, never the clock.** A Zocdoc start carries the *provider's* own UTC
 * offset, and these screens deliberately show that wall clock rather than the operator's — an
 * operator checking a booking needs the time the patient and the practice agreed on
 * (`docs/ZOCDOC_TIME_DIFFERENCES.md` §1). So the day and time are read out of the string's own
 * characters and never handed to a locale that would apply the browser's zone.
 */

/** `HH:mm` in some fixed zone → `h:mm a`, e.g. `14:30` → `2:30 pm`. No zone arithmetic. */
export function formatWallClockTime(time: string): string {
  const [rawHour, minute] = time.split(':')
  const hour = Number(rawHour)
  if (!Number.isInteger(hour) || minute === undefined) return time
  const suffix = hour < 12 ? 'am' : 'pm'
  const twelve = hour % 12 === 0 ? 12 : hour % 12
  return `${twelve}:${minute} ${suffix}`
}

/** `YYYY-MM-DD` → `MM/DD/YYYY`. Character surgery, so a calendar date cannot shift a day. */
export function formatCalendarDay(day: string): string {
  const [year, month, date] = day.split('-')
  return year && month && date ? `${month}/${date}/${year}` : day
}

/**
 * A Zocdoc start (`2026-09-22T10:00:00-04:00`) → `{ day: '09/22/2026', time: '10:00 am' }`, read at
 * the offset Zocdoc sent.
 */
export function splitZocdocStart(startAt?: string): { day: string; time: string } | null {
  if (!startAt || startAt.length < 16) return null
  return {
    day: formatCalendarDay(startAt.slice(0, 10)),
    time: formatWallClockTime(startAt.slice(11, 16)),
  }
}

/** A Zocdoc start as one line — `09/22/2026 10:00 am` — for prose and confirmations. */
export function formatZocdocStart(startAt?: string): string | null {
  const parts = splitZocdocStart(startAt)
  return parts ? `${parts.day} ${parts.time}` : null
}

/**
 * A date of birth → `MM/DD/YYYY`, **never** zone-shifted.
 *
 * The same call the Schedule page makes (`formatDateTime(birthDate, '/', false, true)`): a birth
 * date is a calendar date, and converting it to a zone moves it a day for anybody west of the
 * clinic.
 */
export function formatDob(dob?: string | null): string | null {
  return dob ? formatDateTime(dob, '/', false, true) : null
}
